import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fromUrlSafeBase64, inflate } from 'utils/compressor';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { setAuthHeaderToken, setAuthTenantId } from 'libs/Axios';
import { useDispatch } from 'react-redux';
import { RoleType, setAccessToken, setRole, setSelectedTenant, setUserBehaviourRole } from 'store/reducer/authReducer';
import { useGetRoleAccessToken } from 'services/v1/Auth';
import { useGetMyPersonalizationLogin } from 'services/v1/Tenant/ConfigurationService';
import { getUserRoleBehaviour } from './SelectRolePage';

export function ExternalRedirectPage(): JSX.Element {
  const { tenants } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const { mutateAsync: getRoleAccessToken } = useGetRoleAccessToken();
  const { mutateAsync: fetchPersonalization } = useGetMyPersonalizationLogin();
  const [searchParams] = useSearchParams();
  const externalParam = searchParams.get('ext');
  const navigate = useNavigate();

  useEffect(() => {
    // Define an async function inside the effect
    const fetchRoleAccessToken = async () => {
      if (externalParam && tenants.length > 0) {
        try {
          const b64 = fromUrlSafeBase64(externalParam);
          const decodedData: {
            tenantId: string;
            url: string;
          } = inflate(b64);

          const selectedTenants = tenants.filter((tenant) => tenant.tenant.id === decodedData.tenantId);

          if (!!selectedTenants && selectedTenants.length === 1) {
            const selectedTenant = selectedTenants[0];
            const roleBehaviour = getUserRoleBehaviour(selectedTenant.roleType, selectedTenant) as RoleType;
            try {
              // Await inside the async function
              const res = await getRoleAccessToken({
                roleName: selectedTenant.tenant.roleName,
                tenantId: selectedTenant.tenant.id,
              });
              const roleAccessToken = res.data.data;

              setAuthHeaderToken(roleAccessToken);
              dispatch(setAccessToken(roleAccessToken));
              dispatch(setRole(selectedTenant.roleType));
              dispatch(setSelectedTenant(selectedTenant));
              setAuthTenantId(selectedTenant.tenant.id);
              dispatch(setUserBehaviourRole(roleBehaviour));

              await fetchPersonalization();

              // Navigate to the desired URL
              navigate(decodedData.url, {
                replace: true,
              });
            } catch (error) {
              // Handle errors as needed
              navigate('/', { replace: true });
            }
          } else {
            // Handle case where selected tenant is not found
            navigate('/', { replace: true });
          }
        } catch (parseError) {
          // Handle parsing errors
          navigate('/', { replace: true });
        }
      }
    };

    // Call the async function
    if (externalParam) {
      fetchRoleAccessToken();
    } else {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalParam, tenants]);

  return (
    <Stack sx={{ height: '100vh' }} spacing={0} alignItems='center' justifyContent='center'>
      <Stack spacing={2}>
        <ActivityIndicator />
        <Typography variant='h4' sx={{ mb: 2 }}>
          Validating your access...
        </Typography>
      </Stack>
    </Stack>
  );
}
